// @flow

/* eslint object-shorthand:0 */

import ajax from 'app/common/lib/Ajax';
import changeWindowLocation from 'app/common/lib/util/changeWindowLocation';
import context from 'app/common/lib/context';
import Csrf from 'app/common/lib/Csrf';
import DenzelError from 'app/common/lib/DenzelError';
import errorUtil from 'app/common/lib/errorUtil';
import focusUtil from 'app/common/lib/focusUtil';
import i18n from 'app/common/lib/i18n';
import isEqual from 'lodash/isEqual';
import Logging from 'app/common/lib/Logging';
import LogToServer from 'app/common/lib/LogToServer';
import omit from 'lodash/omit';
import Pc from 'app/common/lib/Pc';
import pick from 'lodash/pick';
import pubSub from 'app/common/lib/PubSub';
import randomTraceId from 'app/packages/trace-logger/utils/generateTraceId';
import ResourceFactory from 'app/common/lib/ResourceFactory/browser';
import resourceResponse from 'app/common/lib/PureReactResourceResponse';
import state from 'app/common/lib/state';
import store from 'app/common/redux/store/singleton';
import timer from 'app/common/lib/timer';
import urlUtil from 'app/common/lib/urlUtil';
import user from 'app/common/lib/User';
import ValueUtil from 'app/common/lib/ValueUtil';
import { DEBUG_TRACING } from 'app/packages/trace-logger/debugFlags';
import { getResourceUrl } from 'app/packages/full-node-utils';
import { OpenLoginModal } from 'app/common/redux/actions/login';
import { RequestContextFactory } from 'app/common/lib/RequestContext';
import { Timer } from 'app/common/lib/TrackingTimer';

const resourceLog = Logging.getLogger('PureReactResource');

type ResourceCallOptions = {
  async?: boolean,
  showError: boolean,
  timeout: ?number,
  tracingParams?: Object,
};

type ResourceResponse = {
  client_context: Object,
  page_info: Object,
  request_identifier: string,
  bookmarks: string[],
  originHost: string,
};

/**
 * @const
 * @type {string}
 */
// keep this in sync with base_resource.py
const END_BOOKMARK = '-end-';

/**
 * @const
 * @type {number}
 *
 * When making an XHR request, this is the default number of times we will try
 * the attempt.
 */
const RETRY_DEFAULT_TRIES = 1;
const RETRY_DEFAULT_GET_TRIES = 1;

/**
 * @const
 * @type {number}
 *
 * If we are retrying an XHR request, this is the default time (ms) we wait
 * before attempting a second time.
 */
const RETRY_DEFAULT_DELAY = 1000;

/**
 * Resource class that manages the resource options and the ajax requests
 * for new data/html.
 *
 * TODO(chris): Rename this to "P.RemoteResource".
 *
 * @param {string} name The name of the resource
 * @param {Object=} options The options of the resource including these two standard
 *     options:
 *          'bookmarks': {?Array.<string>} The last N bookmarks, if
 *              available.
 * @constructor
 * @implements {IResource}
 */
export default class Resource {
  name: string;
  options: Object;
  xhr: ?XMLHttpRequest;

  constructor(name: string, options: Object) {
    this.name = name;
    if (options) {
      // Deep copy the options so that we don't mutate what the caller passed in.
      this.options = ValueUtil.deepCopy(options);
    } else {
      this.options = {};
    }
  }

  /**
   * This needs to be kept in sync with the key list in webapp/context.py
   * todo(chris): Find some mechanism to automatically keep this in sync.
   */
  getClientToServerKeys(PcRequestSettings: Object): Array<string> {
    const keys = [];
    if (PcRequestSettings.DEBUG) {
      keys.push('is_test_mode', 'request_debug');
    }
    // Only pass advertiser back to server if we're not in
    // test mode.  Otherwise the context has side
    // effects on other tests.
    if (!PcRequestSettings.IS_TEST_MODE) {
      keys.push('advertiser');
    }
    return keys;
  }

  /**
   * Replace all the options for the resource.
   *
   * @return {string}
   */
  getName(): string {
    return this.name;
  }

  /**
   * Replace all the options for the resource.
   */
  setOptions(options: Object): void {
    this.options = options;
  }

  /**
   * Sets a specific option for the resource.
   */
  set(name: string, value: any): void {
    this.options[name] = value;
  }

  /**
   * Get the value of a specific resource option.
   */
  get(name: string): any {
    return this.options[name] || null;
  }

  /**
   * Clone the resource options
   *
   * @param {boolean} removeState Whether to include state options such as bookmarks.
   * @return {!Object} option data used by a resource
   */
  copyOptions(removeState: boolean): Object {
    const options = ValueUtil.deepCopy(this.options);
    if (options && removeState) {
      delete options.bookmarks;
    }
    return options;
  }

  /**
   * Call the resource with the 'get' method.
   */
  callGet(
    params: ResourceCallOptions,
    moduleRenderOptions: Object,
    headers: Object = {},
  ): Promise<ResourceResponse> {
    const { logNetworkTimer, loggerComponentName } = this.options;
    const networkTimer = logNetworkTimer ? new Timer().start() : null;
    if (params && params.tracingParams) {
      const { traceId = null, spanId = null } = params.tracingParams;
      if (traceId) {
        headers['X-B3-TraceId'] = traceId.toString(16);
        // TODO(jchan): Figure out why adding parent span doesn't work
        // if (parentSpanId) {
        //   headers['X-B3-ParentSpanId'] = parentSpanId.toString(16);
        // }
        headers['X-B3-SpanId'] = (spanId && spanId.toString(16)) || randomTraceId().toString(16);
      }
    }

    // If we're in debug mode for the tracelogger, add this header which will
    // force all downstream services to sample as well
    if (DEBUG_TRACING) {
      headers['X-B3-Flags'] = '1';
    }

    return this.call('get', params, moduleRenderOptions, headers).then((response) => {
      if (networkTimer) {
        networkTimer.end();
        LogToServer.logToServer(
          LogToServer.SERVER_LOG_TYPE.NETWORK_LOAD,
          LogToServer.HTTP_METHOD.POST,
          {
            timing: networkTimer.getCompletedTime(),
            component: loggerComponentName || this.name,
          },
        );
      }
      return response;
    });
  }

  /**
   * Call the resource with the 'create' method.
   */
  callCreate(
    params: ResourceCallOptions,
    moduleRenderOptions: Object,
    headers: Object,
  ): Promise<ResourceResponse> {
    return this.call('create', params, moduleRenderOptions, headers);
  }

  /**
   * Call the resource with the 'update' method.
   */
  callUpdate(
    params: ResourceCallOptions,
    moduleRenderOptions: Object,
    headers: Object,
  ): Promise<ResourceResponse> {
    return this.call('update', params, moduleRenderOptions, headers);
  }

  /**
   * Call the resource with the 'delete' method.
   */
  callDelete(
    params: ResourceCallOptions,
    moduleRenderOptions: Object,
    headers: Object,
  ): Promise<ResourceResponse> {
    return this.call('delete', params, moduleRenderOptions, headers);
  }

  /**
   * Calculate whether the URL is short enough for a HTTP GET request.
   */
  _isUrlShortEnoughForHttpGet(path: string, requestData: Object): boolean {
    const potentialUrl = path + '?' + urlUtil.getQueryStringFromObject(requestData);

    // 2000 is the de-facto length limit of a UTF8-encoded URL.
    // http://stackoverflow.com/questions/417142/what-is-the-maximum-length-of-a-url-in-different-browsers
    return potentialUrl.length <= 2000;
  }

  /**
   * @param {string} method
   * @param {!ResourceCallOptions=} callParams
   * @param {!ModuleRenderOptions=} moduleRenderOptions The module to render
   * @param {Object=} headers
   * @return {!Promise.<!ResourceResponse>}
   * @private
   */
  call(
    method: string,
    callParams: ResourceCallOptions,
    moduleRenderOptions?: Object,
    headers?: Object,
  ): Promise<ResourceResponse> {
    const async: boolean = callParams && callParams.async !== undefined ? callParams.async : true;

    const timeoutSeconds: ?number = (callParams && callParams.timeout) || null;

    const promise: Promise<ResourceResponse> = this._call(
      method,
      async,
      timeoutSeconds,
      moduleRenderOptions,
      headers,
    );

    //
    // Attach callbacks
    //
    promise.then(
      (_resourceResponse) => {
        this.onResponseSuccess(_resourceResponse);
      },
      (error) => {
        this.onResponseError(error);
      },
    );

    /**
     * Check if user is write banned before invoking regular error callback.
     * Replaces original error dialog with write ban password reset dialog.
     * Necessary for resources called with an existing callParams.error function,
     * like BoardFollowResource and UserFollowResource.
     */
    promise.then(undefined, (error) => {
      // WRITE_BANNED API response code
      if (error && error.api_error_code === 19 && user.isWriteBanned()) {
        console.error('User is write banned'); // eslint-disable-line no-console
      } else if (callParams && callParams.showError) {
        // On devapps: add extra information to error dialogs
        const heading = process.env.NODE_ENV === 'development' ? this.name + ' error' : undefined;
        errorUtil.showError(error.message, null, heading);
      }
    });

    return promise;
  }

  getResourceUrl(name: string, method: string): string {
    const requestContext = RequestContextFactory.fromGlobalContext();
    return getResourceUrl({
      isAuthenticated: requestContext.isAuthenticated,
      isBot: requestContext.isBot,
      experimentActivator: (name) => requestContext.experiments.v2ActivateExperiment(name),
      resourceName: name,
      seoOrUnauthExperimentActivator: (name) =>
        requestContext.seoUnauthExperiments.v2ActivateExperiment(name),
      method,
    });
  }

  /**
   * @param {string} method
   * @param {boolean} async
   * @param {?number} timeoutSeconds Pass null for no timeout
   * @param {!ModuleRenderOptions=} moduleRenderOptions The module to render
   * @param {Object=} headers
   * @return {!Promise.<!ResourceResponse>}
   * @private
   */
  _call(
    method: string,
    async: boolean,
    timeoutSeconds: ?number,
    moduleRenderOptions?: Object,
    headers?: Object,
  ): Promise<any> {
    this.abort();

    // should not retry for non-get requests
    let retriesLeft = RETRY_DEFAULT_TRIES;
    if (method === 'get') {
      retriesLeft = RETRY_DEFAULT_GET_TRIES;
    }
    let retryAfter = RETRY_DEFAULT_DELAY;
    const resourceDict = {
      options: this.options,
      context: pick(context.instance, this.getClientToServerKeys(Pc)),
    };

    const resourceUrl = this.getResourceUrl(this.name, method);
    if (moduleRenderOptions) {
      retriesLeft = moduleRenderOptions.retries || retriesLeft;
      retryAfter = moduleRenderOptions.retryAfter || retryAfter;
    }

    // The request body must be form-encodable.
    const requestData: Object = {
      source_url: urlUtil.getCurrentUrl(),
      data: JSON.stringify(resourceDict),
    };

    const activeTarget = state.getActiveTarget();
    if (activeTarget) {
      requestData.module_path = activeTarget.getFullName();
    }

    const useHttpGet =
      method === 'get' && this._isUrlShortEnoughForHttpGet(resourceUrl, requestData);

    // header to indicate whether a tab is active or inactive
    type Headers = { [string]: string };
    const requestHeaders: Headers = {
      'X-Pinterest-AppState': focusUtil.windowIsInFocus ? 'active' : 'background',
    };

    if (context.instance.experiment_hash) {
      // pass up the hash of experiments to prevent resending
      requestHeaders['X-Pinterest-ExperimentHash'] = context.instance.experiment_hash;
    }

    Object.assign(requestHeaders, headers);

    return new Promise((resolve, reject) => {
      /** @type {?string} */
      let lastError = null;

      const xhrOptions = {
        cache: false,
        url: resourceUrl,
        async: async,
        data: requestData,
        dataType: 'json',
        headers: requestHeaders,
        timeout: timeoutSeconds,
        type: useHttpGet ? 'GET' : 'POST',
      };

      const requestOptions = {
        url: resourceUrl,
        requestData: requestData,
        resourceDict: resourceDict,
      };

      const retry = () => {
        retriesLeft -= 1;
        if (retriesLeft < 0) {
          // we have failed, reject the last error we saw
          return reject(lastError);
        }

        // still going strong!
        // we don't use a promise here because we instead want
        // an instance of the XHR object for aborting purposes
        this.xhr = this._createXhr(
          requestOptions,
          xhrOptions,
          (ok) => resolve(ok),
          (err, preventRetry) => {
            if (preventRetry) {
              return reject(err);
            }
            lastError = err;
            return retry.again();
          },
        );
        return this.xhr;
      };

      retry.again = function() {
        timer.setTimeout(retry, retryAfter);
      };

      retry.start = function() {
        retry();
      };

      retry.start();
    });
  }

  /**
   * Creates a resource XHR object, tuned for Pinterest's response codes
   * This is because we return 404, 401, and assorted other "valid" status
   * codes. This makes _call a bit easier to follow, while this handles
   * the pure jQuery <=> Python interaction
   *
   * Even though we have written in a handler for error(), we are not
   * handling it until the complete() handler. This is because jQuery thinks
   * some calls are an error which to us, are not.
   *
   * @param {Object} requestOptions Container with URL, resource, and request data
   * @param {Object} ajaxOptions The $.ajax options
   * @param {Function} successCb The success callback
   * @param {Function} errorCb The error callback
   * @return {Object} xhr A jQuery XHR object that is cancelable
   */
  _createXhr(
    requestOptions: Object,
    ajaxOptions: Object,
    successCb: Function,
    errorCb: Function,
  ): XMLHttpRequest {
    let savedErrorThrown = null;
    const xhrUrl = requestOptions.url;
    const resourceDict = requestOptions.resourceDict;
    const requestData = requestOptions.requestData;

    /**
     * Used to capture jQuery's error events
     * @param {!jQuery.jqXHR} jqXHR
     * @param {string} textStatus
     * @param {string} errorThrown
     */
    ajaxOptions.error = function(jqXHR, textStatus, errorThrown) {
      savedErrorThrown = errorThrown;
    };

    /**
     * @param {!jQuery.jqXHR} jqXHR
     * @param {string} textStatus
     */
    ajaxOptions.complete = function(jqXHR, textStatus) {
      if (textStatus === 'abort') {
        errorCb('Resource call aborted', true);
        return;
      }

      const errorData = {
        resource_name: this.name,
        response_headers: jqXHR.getAllResponseHeaders(),
        http_status: jqXHR.status,
        text_status: textStatus,
        response_text: jqXHR.responseText,
        error_thrown: savedErrorThrown,
        xhrUrl,
        userAgent: null,
        payload: null,
      };

      // The HTTP status will be 0 if there's a connectivity error
      // or if the request was cancelled due to a navigation.
      if (jqXHR.status === 0) {
        errorData.userAgent = window.navigator.userAgent;

        const jsonParsedRequestData = { ...requestData };
        jsonParsedRequestData.data = JSON.parse(jsonParsedRequestData.data);
        errorData.payload = jsonParsedRequestData;

        resourceLog.error('Connectivity error or cancelled by navigation', errorData);
        errorCb(new Error(i18n._('Connectivity error or cancelled by navigation')), true);
        return;
      }

      /** @type {ResourceResponse} */
      let _resourceResponse = null;

      /** @type {DenzelError} */
      let denzelError = null;

      // Check and see if we have a JSON response anyway.
      // In most cases, there wasn't an actual server error,
      // but rather the response code was a 500/403 etc.
      try {
        const responsePlainObject = JSON.parse(jqXHR.responseText);
        if (!(responsePlainObject instanceof Object)) {
          throw Error('JSON.parse did not return an object');
        }
        _resourceResponse = resourceResponse.fromPlainObject(responsePlainObject);
        _resourceResponse.originHost =
          jqXHR.getResponseHeader('pinterest-generated-by') || undefined;
        denzelError = _resourceResponse.resource_response.error;
      } catch (e) {
        denzelError = new DenzelError();
        denzelError.message = i18n.commonStrings.defaultErrorMessage;
        denzelError.original_message = e.message;
        denzelError.http_status = jqXHR.status;

        resourceLog.error(
          'Failed to parse resource response. HTTP ' + jqXHR.status,
          e.message,
          errorData,
        );
      }

      if (denzelError) {
        if (denzelError.http_status === 403 && !user.isAuthenticated()) {
          // A 403 response can be caused by:
          // 1) the user being unauthenticated and trying to perform an action
          //    that requires authentication
          // 2) a CSRF error
          // If there is a 403 error and the user is unauth, then the reason is
          // probably 1).   Redirect them to the login page.

          const currentUrl = urlUtil.getCurrentUrl();
          const currentQueryParams = urlUtil.getQueryStringParamsFromUrl(currentUrl);
          if (currentUrl.startsWith('/login') && currentQueryParams.next) {
            // Attempt to clear all csrftokens for users in this state, even if
            // they only have 1 csrftoken cookie value
            Csrf.cleanUpCookie();
            Csrf.clearSecondCookie();
          } else {
            changeWindowLocation(urlUtil.getLoginUrl(), false);
          }
          return;
        } else if (
          denzelError.http_status === 401 &&
          user.isLimitedLogin() &&
          !(
            resourceDict &&
            resourceDict.options &&
            resourceDict.options.disable_auth_failure_redirect
          )
        ) {
          // If the error is a 401 then this request required authentication.
          // Redirect the user to the login page only if this came from
          // the authentication layer in webapp.
          // TODO(nadine): Generate JS consts for these API magic numbers.
          if (store.instance && this.name !== 'UserSessionResource') {
            store.instance.dispatch(OpenLoginModal);
          }
          errorCb(denzelError);
          return;
        }

        // If the response contains both an error and a module tree,
        // treat the response as a successful response.  We want to load the module
        // tree since it will contain some HTML with an error message.
        if (_resourceResponse && _resourceResponse.moduleTree) {
          successCb(_resourceResponse);
        } else {
          errorCb(denzelError);
        }
      } else if (_resourceResponse) {
        successCb(_resourceResponse);
      } else {
        resourceLog.error('no error and no resource response', errorData);
      }

      this.xhr = null;
    }.bind(this);

    return ajax(ajaxOptions);
  }

  /**
   * Abort the pending xhr, if there is one.
   */
  abort() {
    if (this.xhr) {
      this.xhr.abort();
      this.xhr = null;
    }
  }

  /**
   * Reset our bookmark by clearing the bookmarks propery from our options.
   */
  resetBookmark(): void {
    // TODO: Investigate why a resource can be missing the options member.
    if (this.options) {
      delete this.options.bookmarks;
    }
  }

  /**
   * Update the bookmarks for the resource
   *
   * @param {Array.<string>} bookmarks  a list of most recent bookmarks
   */
  setBookmarks(bookmarks: string[]): void {
    this.set('bookmarks', bookmarks);
  }

  /**
   * Get the latest bookmark we have fetched
   *
   * @return {?*} the most recent bookmark, or null if the resource
   *              has no bookmarks
   */
  getLatestBookmark(): ?Object {
    const bookmarks = this.options.bookmarks;
    if (bookmarks) {
      return bookmarks[bookmarks.length - 1];
    } else {
      return undefined;
    }
  }

  /**
   * Have we fetched the last item?
   *
   * @return {boolean} Returns:
   *  - true if there are no more items to fetch
   *    (we have an "end bookmark")
   *  - false if there are more items to fetch
   *    (we have a regular bookmark)
   *  - false if we haven't yet fetched the first page of items
   *    and don't know whether there are more to fetch
   */
  atEnd() {
    return this.getLatestBookmark() === END_BOOKMARK;
  }

  /**
   * Handler for a successful ajax response. Updates the resource's
   * options.
   *
   * @private
   * @param {!ResourceResponse} _resourceResponse
   */
  onResponseSuccess(_resourceResponse: ResourceResponse): void {
    // Check if the app version has changed
    if (_resourceResponse.client_context) {
      // TODO(jeremy/chris): we should switch to retrieving this from the
      // headers if possible
      pubSub.instance.publish(PCONST.PUBSUB_CHANNEL_SITE, PCONST.PUBSUB_TOPIC_APP_VERSION, {
        version: _resourceResponse.client_context.app_version,
        originHost: _resourceResponse.originHost,
      });

      const eventLogInfo = _resourceResponse.client_context.event_log_info;
      if (eventLogInfo) {
        pubSub.instance.publish(
          PCONST.PUBSUB_CHANNEL_CONTEXT_LOGGING,
          PCONST.PUBSUB_TOPIC_CONTEXT_LOG_PLAIN_OBJECT,
          eventLogInfo,
        );
      }
      const placedExperiences = _resourceResponse.client_context.placed_experiences;
      if (placedExperiences) {
        pubSub.instance.publish(
          PCONST.PUBSUB_CHANNEL_SITE,
          PCONST.PUBSUB_TOPIC_PLACED_EXPERIENCES_FROM_SERVER,
          placedExperiences,
        );
      }

      const { triggerable_experiments, experiment_hash } = _resourceResponse.client_context;
      if (experiment_hash !== context.instance.experiment_hash) {
        // force persistence of the previous experiments data
        _resourceResponse.client_context.triggerable_experiments = triggerable_experiments;
        _resourceResponse.client_context.experiment_hash = experiment_hash;
      }
    }

    // Save the state of the UID for this pageload
    /** @type {string} */
    const currUid = context.instance._uid;
    if (_resourceResponse.client_context) {
      context.set(_resourceResponse.client_context);
    }

    if (currUid) {
      context.instance._uid = currUid;
    }

    if (_resourceResponse.page_info) {
      if (
        _resourceResponse.page_info.title ||
        Object.keys(_resourceResponse.page_info.meta).length
      ) {
        pubSub.instance.publish(
          PCONST.PUBSUB_CHANNEL_SITE,
          PCONST.PUBSUB_TOPIC_PAGE_INFO,
          _resourceResponse.page_info,
        );
      }
    }

    if (_resourceResponse.bookmarks) {
      this.setBookmarks(_resourceResponse.bookmarks);
    }

    pubSub.instance.publish(
      PCONST.PUBSUB_CHANNEL_REQUEST,
      PCONST.PUBSUB_TOPIC_REQUEST_IDENTIFIER,
      _resourceResponse.request_identifier,
    );
  }

  onResponseError(error: Object): void {
    resourceLog.debug('Request error for ' + this.name, error, error.stack_trace);
  }

  /**
   * Sets resource-specific error data for reporting to the server
   *
   * @param {string} fnName (for consistency with Module.getErrorData)
   */
  getErrorData(fnName: string): Object {
    return {
      resource_name: this.name,
    };
  }

  /**
   * Whether this resource has the same options as the other resource.
   *
   * @param {!IResource} other The other resource.
   * @param {boolean=} includeBookmark whether to consider the latest
   *                                   bookmark in the comparison.
   * @return {boolean} Whether the resource options are equal.
   */
  isEqual(other: Resource, includeBookmark?: boolean): boolean {
    if (this.name !== other.name) {
      return false;
    }

    // We do not need to compare all bookmarks, just the latest bookmark
    const thisCopy = omit(this.options, ['bookmarks']);
    const otherCopy = omit(other.options, ['bookmarks']);

    if (includeBookmark) {
      thisCopy.latest_bookmark = this.getLatestBookmark();
      // TODO (ytian): we should fix this sadness
      // - In some cases (e.g., Module.setAsMainModule()), the param passed
      //   in does not implement IResource
      const otherBookmarks = other.options.bookmarks;
      otherCopy.latest_bookmark = otherBookmarks && otherBookmarks[otherBookmarks.length - 1];
    }

    return isEqual(thisCopy, otherCopy);
  }

  /**
   * @return {string}
   */
  toString() {
    const options = omit(this.options, ['bookmarks', 'field_set_key']);
    return this.name + '(' + urlUtil.toScrubbedArgumentString(options) + ')';
  }

  /**
   * @return {boolean}
   */
  isCalling() {
    return !!this.xhr;
  }
}

// set the default resource constructor to Resource
ResourceFactory.setDefaultConstructorFn(Resource);
